/**
 * Header Component
 * Main navigation header with context-aware navigation
 */

import { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, usePreferencesStore } from '@schoolidb/store';
import { CountrySelector } from './CountrySelector';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const theme = usePreferencesStore((state) => state.theme);
  const setTheme = usePreferencesStore((state) => state.setTheme);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Determine which nav link to show based on current page
  const isAISearchPage = location.pathname === '/ai-search';
  const isSearchPage = location.pathname === '/search';

  return (
    <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-50">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="School iDB" className="h-8 w-8" />
            <span className="text-2xl font-bold text-primary-800 dark:text-white">School <span className="text-accent-500">iDB</span></span>
          </Link>

          {/* Desktop Navigation - Context aware */}
          <nav className="hidden md:flex items-center gap-6">
            {isAISearchPage ? (
              <NavLink
                to="/search"
                className="text-sm font-medium text-gray-700 hover:text-accent-500 dark:text-gray-300 transition-colors"
              >
                🔍 Normal Search
              </NavLink>
            ) : (
              <NavLink
                to="/ai-search"
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-accent-500'
                      : 'text-gray-700 hover:text-accent-500 dark:text-gray-300'
                  }`
                }
              >
                🤖 AI Search
              </NavLink>
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-4">
            <CountrySelector />
            
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className="text-sm text-gray-700 hover:text-accent-500 dark:text-gray-300"
                >
                  {user?.name || 'Dashboard'}
                </Link>
                <button onClick={handleLogout} className="btn-secondary text-sm">
                  Logout
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" className="btn-secondary text-sm">
                  Login
                </Link>
                <Link to="/signup" className="btn-accent text-sm">
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t dark:border-gray-700">
            <nav className="flex flex-col gap-2">
              {isAISearchPage ? (
                <NavLink
                  to="/search"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
                >
                  🔍 Normal Search
                </NavLink>
              ) : (
                <NavLink
                  to="/ai-search"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg ${
                      isActive
                        ? 'bg-accent-50 text-accent-500 dark:bg-accent-900/20'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`
                  }
                >
                  🤖 AI Search
                </NavLink>
              )}
              <hr className="my-2 dark:border-gray-700" />
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-2 text-left text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 text-accent-500 hover:bg-accent-50 dark:hover:bg-accent-900/20 rounded-lg"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
